import { formatHumanName } from '@medplum/core'
import { Coverage, Extension, Patient } from '@medplum/fhirtypes'
import {
  CoverageTemplate,
  InsuranceFormData,
  InsurancePlanSnapshot,
  InsuranceRank,
} from '../types/insuranceTypes'

export type ResolvedCoverageSlots = {
  primary?: Coverage
  secondary?: Coverage
  tertiary?: Coverage
}

const COVERAGE_DEFAULT_TAG_SYSTEM = 'https://behuman.health/fhir/CodeSystem/coverage-tags'
const COVERAGE_DEFAULT_TAG_CODE = 'default'
export const COVERAGE_STATE_TAG_SYSTEM = 'https://behuman.health/fhir/CodeSystem/coverage-state'
export const COVERAGE_PLAN_NAME_EXTENSION_URL = 'https://behuman.health/coverage-plan-name'
export const COVERAGE_PLAN_PRODUCT_TYPE_EXTENSION_URL =
  'https://behuman.health/coverage-plan-product-type'
export const COVERAGE_PLAN_ID_EXTENSION_URL = 'https://behuman.health/coverage-plan-id'
export const COVERAGE_PLAN_LOB_CATEGORY_EXTENSION_URL =
  'https://behuman.health/coverage-plan-lob-category'

export const convertToFHIRDate = (mmddyyyy?: string): string | undefined => {
  if (!mmddyyyy || mmddyyyy.length !== 10) return undefined

  const [month, day, year] = mmddyyyy.split('-')
  return `${year}-${month}-${day}`
}

export const convertFromISODate = (yyyymmdd?: string): string => {
  if (!yyyymmdd || yyyymmdd.length !== 10) return ''
  const [year, month, day] = yyyymmdd.split('-')
  return `${month}/${day}/${year}`
}

export const validateNameField = (value?: string): string | null => {
  if (!value || value.trim().length === 0) {
    return null
  }

  const trimmedValue = value.trim()

  if (trimmedValue.length < 2) {
    return 'Name must be at least 2 characters'
  }

  if (trimmedValue.length > 100) {
    return 'Name must be 100 characters or less'
  }

  const nameRegex = /^[A-Za-zÀ-ÿ\s'-]+$/
  if (!nameRegex.test(trimmedValue)) {
    return 'Name can only contain letters, spaces, apostrophes, and hyphens'
  }

  const validCharRegex = /[A-Za-zÀ-ÿ]/
  if (!validCharRegex.test(trimmedValue)) {
    return 'Name must contain at least one letter'
  }

  return null
}

export const COVERAGE_DEFAULT_TAG = {
  system: COVERAGE_DEFAULT_TAG_SYSTEM,
  code: COVERAGE_DEFAULT_TAG_CODE,
  display: 'Default coverage',
}

export const normalizeCoverageState = (value?: string | null): string | undefined => {
  const normalized = value?.trim().toUpperCase()
  return normalized && /^[A-Z]{2}$/.test(normalized) ? normalized : undefined
}

export const getCoverageState = (coverage: Coverage): string | undefined => {
  return normalizeCoverageState(
    coverage.meta?.tag?.find((tag) => tag.system === COVERAGE_STATE_TAG_SYSTEM)?.code,
  )
}

export const getCoveragePlanSnapshot = (coverage: Coverage): InsurancePlanSnapshot | null => {
  const planName = coverage.extension?.find(
    (ext) => ext.url === COVERAGE_PLAN_NAME_EXTENSION_URL,
  )?.valueString
  const productType = coverage.extension?.find(
    (ext) => ext.url === COVERAGE_PLAN_PRODUCT_TYPE_EXTENSION_URL,
  )?.valueString
  const planId = coverage.extension?.find(
    (ext) => ext.url === COVERAGE_PLAN_ID_EXTENSION_URL,
  )?.valueString
  const lobCategory = coverage.extension?.find(
    (ext) => ext.url === COVERAGE_PLAN_LOB_CATEGORY_EXTENSION_URL,
  )?.valueString

  if (!planName && !productType && !planId && !lobCategory) {
    return null
  }

  return {
    planName: planName || '',
    productType: productType || '',
    planId: planId || '',
    lobCategory: lobCategory || '',
  }
}

export const applyCoverageInsuranceSnapshot = (
  coverage: Coverage,
  input: {
    coverageState?: string | null
    planName?: string
    productType?: string
    planId?: string
    lobCategory?: string
  },
): Coverage => {
  const nextTags = (coverage.meta?.tag ?? []).filter(
    (tag) => tag.system !== COVERAGE_STATE_TAG_SYSTEM,
  )
  const normalizedState = normalizeCoverageState(input.coverageState)
  if (normalizedState) {
    nextTags.push({
      system: COVERAGE_STATE_TAG_SYSTEM,
      code: normalizedState,
    })
  }

  const preservedExtensions = (coverage.extension ?? []).filter(
    (ext) =>
      ext.url !== COVERAGE_PLAN_NAME_EXTENSION_URL &&
      ext.url !== COVERAGE_PLAN_PRODUCT_TYPE_EXTENSION_URL &&
      ext.url !== COVERAGE_PLAN_ID_EXTENSION_URL &&
      ext.url !== COVERAGE_PLAN_LOB_CATEGORY_EXTENSION_URL,
  )

  const snapshotExtensions: Extension[] = [
    input.planName
      ? {
          url: COVERAGE_PLAN_NAME_EXTENSION_URL,
          valueString: input.planName,
        }
      : undefined,
    input.productType
      ? {
          url: COVERAGE_PLAN_PRODUCT_TYPE_EXTENSION_URL,
          valueString: input.productType,
        }
      : undefined,
    input.planId
      ? {
          url: COVERAGE_PLAN_ID_EXTENSION_URL,
          valueString: input.planId,
        }
      : undefined,
    input.lobCategory
      ? {
          url: COVERAGE_PLAN_LOB_CATEGORY_EXTENSION_URL,
          valueString: input.lobCategory,
        }
      : undefined,
  ].filter((extension) => extension !== undefined)

  return {
    ...coverage,
    meta: {
      ...coverage.meta,
      tag: nextTags,
    },
    extension: [...preservedExtensions, ...snapshotExtensions],
  }
}

export const getOrderFromRank = (rank: InsuranceRank): number => {
  switch (rank) {
    case 'Primary':
      return 1
    case 'Secondary':
      return 2
    case 'Tertiary':
      return 3
    default:
      return 1
  }
}

export const getInsuranceRankByOrder = (order?: number): InsuranceRank => {
  switch (order) {
    case 2:
      return 'Secondary'
    case 3:
      return 'Tertiary'
    default:
      return 'Primary'
  }
}

export const getInsuranceNameByIdx = (idx: number): string => {
  switch (idx) {
    case 1:
      return 'Primary'
    case 2:
      return 'Secondary'
    case 3:
      return 'Tertiary'
    default:
      return ''
  }
}

export const normalizeResolvedCoverageSlots = (resolved: ResolvedCoverageSlots): Coverage[] =>
  [
    resolved.primary ? { ...resolved.primary, order: 1 } : undefined,
    resolved.secondary ? { ...resolved.secondary, order: 2 } : undefined,
    resolved.tertiary ? { ...resolved.tertiary, order: 3 } : undefined,
  ].filter(Boolean) as Coverage[]

export const createCoverageFromTemplate = (
  template: CoverageTemplate,
  patient: Patient,
  organizations: { id: string; name: string }[] = [],
): Coverage => {
  const selectedOrg = organizations.find((org) => org.id === template.payorId)
  const order = getOrderFromRank(template.insuranceRank)
  const coverage: Coverage = {
    resourceType: 'Coverage',
    identifier: [{ value: template.memberId }],
    status: 'active',
    subscriberId: template.memberId,
    beneficiary: {
      reference: `Patient/${patient.id}`,
      display: patient.name?.[0] ? formatHumanName(patient.name[0]) : '',
    },
    payor: [{ display: selectedOrg?.name || template.payorName }],
    class: [
      {
        type: {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
              code: 'plan',
              display: 'Plan',
            },
          ],
          text: 'Plan',
        },
        value: template.payorName,
        name: template.payorName,
      },
    ],
    order,
    meta: {
      tag: [COVERAGE_DEFAULT_TAG],
    },
  }

  if (template.groupId) {
    coverage.class?.push({
      type: {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
            code: 'group',
            display: 'Group',
          },
        ],
        text: 'Group',
      },
      value: template.groupId,
      name: template.groupId,
    })
  }

  if (template.payorId && coverage.payor[0]) {
    coverage.payor[0].identifier = { value: template.payorId }
  }

  if (template.coverageType) {
    const displayCoverageType =
      template.coverageType.charAt(0).toUpperCase() + template.coverageType.slice(1).toLowerCase()

    coverage.relationship = {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
          code: template.coverageType,
          display: displayCoverageType,
        },
      ],
      text: displayCoverageType,
    }
    coverage.type = { text: displayCoverageType }
  }

  if (template.coverageType && template.coverageType.toLowerCase() !== 'self') {
    const extensions = []

    if (template.subscriberName) {
      extensions.push({
        url: 'https://behuman.health/coverage-subscriber-name',
        valueString: template.subscriberName,
      })
    }

    if (template.subscriberDob) {
      extensions.push({
        url: 'https://behuman.health/coverage-subscriber-dob',
        valueDate: template.subscriberDob,
      })
    }

    if (template.subscriberMemberId) {
      extensions.push({
        url: 'https://behuman.health/coverage-subscriber-member-id',
        valueString: template.subscriberMemberId,
      })
    }

    if (extensions.length > 0) {
      coverage.extension = extensions
    }
  }

  return applyCoverageInsuranceSnapshot(coverage, {
    coverageState: template.coverageState,
    planName: template.planName,
    productType: template.productType,
    planId: template.planId,
    lobCategory: template.lobCategory,
  })
}

export const hasDefaultCoverageTag = (coverage: Coverage): boolean =>
  Boolean(
    coverage.meta?.tag?.some(
      (tag) =>
        tag.code === COVERAGE_DEFAULT_TAG_CODE &&
        (!tag.system || tag.system === COVERAGE_DEFAULT_TAG_SYSTEM),
    ),
  )

export const withDefaultCoverageTag = (coverage: Coverage): Coverage => {
  const existingTags = coverage.meta?.tag || []
  const withoutDuplicate = existingTags.filter(
    (tag) =>
      !(
        tag.code === COVERAGE_DEFAULT_TAG_CODE &&
        (!tag.system || tag.system === COVERAGE_DEFAULT_TAG_SYSTEM)
      ),
  )

  return {
    ...coverage,
    meta: {
      ...coverage.meta,
      tag: [...withoutDuplicate, COVERAGE_DEFAULT_TAG],
    },
  }
}

export const withoutDefaultCoverageTag = (coverage: Coverage): Coverage => {
  const existingTags = coverage.meta?.tag || []
  const nextTags = existingTags.filter(
    (tag) =>
      !(
        tag.code === COVERAGE_DEFAULT_TAG_CODE &&
        (!tag.system || tag.system === COVERAGE_DEFAULT_TAG_SYSTEM)
      ),
  )

  return {
    ...coverage,
    meta: {
      ...coverage.meta,
      tag: nextTags,
    },
  }
}

export const sortCoveragesByLastUpdatedDesc = <T extends Coverage>(coverages: T[]): T[] =>
  [...coverages].sort((a, b) => {
    const dateA = a.meta?.lastUpdated ? new Date(a.meta.lastUpdated).getTime() : 0
    const dateB = b.meta?.lastUpdated ? new Date(b.meta.lastUpdated).getTime() : 0
    return dateB - dateA
  })

export const selectCoverageForOrder = <T extends Coverage>(
  coverages: T[],
  order: number,
): T | undefined => {
  const candidates = coverages.filter(
    (coverage) => coverage.order === order && coverage.status === 'active',
  )
  if (!candidates.length) {
    return undefined
  }

  const defaultCandidates = sortCoveragesByLastUpdatedDesc(
    candidates.filter((coverage) => hasDefaultCoverageTag(coverage)),
  )

  if (defaultCandidates.length) {
    return defaultCandidates[0]
  }

  return sortCoveragesByLastUpdatedDesc(candidates)[0]
}

export const getInsuranceDisplayData = (coverage: Coverage) => {
  const plan =
    coverage.class?.find((c) => c.type?.coding?.find((t) => t.code === 'plan'))?.value ||
    coverage.payor?.[0]?.display ||
    ''
  const group =
    coverage.class?.find((c) => c.type?.coding?.find((t) => t.code === 'group'))?.value || ''
  const MEMBER_ID_SYSTEM = 'https://behuman.health/fhir/coverage-member-id'
  // `subscriberId` last, and only when the patient IS the subscriber. It is
  // FHIR's insurer ID *for the subscriber*, which is the patient's own member
  // ID only on self coverage — `getSubscriberMemberId` below reads the same
  // field for the opposite purpose, precisely because it identifies the
  // policyholder on a dependent's plan. Reading it unconditionally printed the
  // policyholder's ID as a dependent's member ID, and a lab bills on that
  // field. Anything other than an explicit `self` leaves it blank: a blank
  // member ID gets queried, a wrong one gets billed to the wrong person.
  // Every coding, not just the first: `relationship` is a CodeableConcept, so an
  // import can carry a payer-specific coding ahead of HL7's `self` and reading
  // `[0]` alone would blank a member ID the patient is entitled to.
  const patientIsSubscriber = coverage.relationship?.coding?.some(
    (coding) => coding.code?.toLowerCase() === 'self',
  )
  const subscriberIdIfSelf = patientIsSubscriber ? coverage.subscriberId : undefined
  const memberIdValue =
    coverage.identifier?.find((i) => i.system === MEMBER_ID_SYSTEM)?.value ||
    coverage.identifier?.find((i) => !i.system)?.value ||
    subscriberIdIfSelf ||
    ''
  const memberId = memberIdValue.length > 30 ? '' : memberIdValue
  const payorName = coverage.payor?.[0]?.display || ''

  return { plan, group, memberId, payorName }
}

export const SUBSCRIBER_NAME_EXTENSION_URL = 'https://behuman.health/coverage-subscriber-name'
export const SUBSCRIBER_DOB_EXTENSION_URL = 'https://behuman.health/coverage-subscriber-dob'
export const SUBSCRIBER_MEMBER_ID_EXTENSION_URL =
  'https://behuman.health/coverage-subscriber-member-id'

export const getSubscriberName = (coverage: Coverage): string | undefined => {
  return coverage.extension?.find((ext) => ext.url === SUBSCRIBER_NAME_EXTENSION_URL)?.valueString
}

export const getSubscriberDob = (coverage: Coverage): string | undefined => {
  return coverage.extension?.find((ext) => ext.url === SUBSCRIBER_DOB_EXTENSION_URL)?.valueDate
}

export const getSubscriberMemberId = (coverage: Coverage): string | undefined => {
  const coverageType = coverage.relationship?.coding?.[0]?.code?.toLowerCase()
  if (coverageType && coverageType !== 'self') {
    return (
      coverage.extension?.find((ext) => ext.url === SUBSCRIBER_MEMBER_ID_EXTENSION_URL)
        ?.valueString ?? coverage.subscriberId
    )
  }
  return undefined
}

export const buildCoverageTemplateFromValues = (
  values: InsuranceFormData,
  editingCoverageId?: string,
  frontImageBase64?: string,
  backImageBase64?: string,
): CoverageTemplate => ({
  id: editingCoverageId || `temp-${Date.now()}`,
  memberId: values.memberId,
  groupId: values.groupId || '',
  insuranceRank: values.insuranceRank,
  coverageType: values.coverageType,
  payorId: values.payorId,
  payorName: values.payorName,
  coverageState: values.coverageState,
  planName: values.planName,
  productType: values.productType,
  planId: values.planId,
  lobCategory: values.lobCategory,
  frontImageBase64,
  backImageBase64,
  subscriberName: values.subscriberName,
  subscriberDob: values.subscriberDob,
  subscriberMemberId: values.subscriberMemberId,
})
